import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { removeAmChartLogo } from 'app/core/shared/amchart4/amchart4.module';
import { MatDialog } from '@angular/material/dialog';
import { ExpandModalDialog } from 'app/modules/well-integrity-management/component/expand-modal/expand-modal.component';
import { UnhealthyWellsResponse } from '../../model/well-integrity-management.type';

am4core.useTheme(am4themes_animated);

@Component({
    selector: 'unhealthy-wells',
    templateUrl: './unhealthy-wells.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, NgTemplateOutlet],
    styleUrls: ['./unhealthy-wells.component.scss'],
})
export class UnhealthyWellsComponent implements OnChanges {
    @Input() expandIcon: any;
    @Input() data: UnhealthyWellsResponse;

    constructor(
        private readonly elementRef: ElementRef,
        private readonly dialog: MatDialog,
        private readonly cdr: ChangeDetectorRef,
        private readonly destroyRef: DestroyRef,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data'] && this.data?.data?.riskRankingTable) {
            this.createPieChart();
            this.cdr.markForCheck();
        }
    }

    openDialog(title: string, componentName: string, functionName: string) {
        const component = document.getElementById(componentName);

        const dialogRef = this.dialog.open(ExpandModalDialog, {
            data: {
                title: title,
                viewComponent: component,
                functionName: functionName,
                apiData: this.data,
            }, // Pass your parameters here
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                console.log(`Dialog result: ${result}`);
            });
    }

    createPieChart() {
        const donutCharts = this.data?.data?.donutCharts || [];

        // Create Active chart (index 0)
        if (donutCharts[0]) {
            const activeData = donutCharts[0];
            const chart = am4core.create('pieChart', am4charts.PieChart);
            chart.padding(5, 10, 5, 0);

            chart.data = [
                {
                    category: 'Healthy',
                    value: activeData.healthyPercentage,
                    color: am4core.color('#4CAF50'),
                },
                {
                    category: 'Unhealthy',
                    value: activeData.unhealthyPercentage,
                    color: am4core.color('#FF5733'),
                },
            ];

            chart.innerRadius = am4core.percent(50);

            const pieSeries = chart.series.push(new am4charts.PieSeries());
            pieSeries.dataFields.value = 'value';
            pieSeries.dataFields.category = 'category';
            pieSeries.slices.template.stroke = am4core.color('#fff');
            pieSeries.slices.template.strokeWidth = 2;
            pieSeries.slices.template.strokeOpacity = 1;
            pieSeries.slices.template.propertyFields.fill = 'color';
            pieSeries.labels.template.text =
                "{values.value.percent.formatNumber('#.#')}%";
            pieSeries.alignLabels = false;
            pieSeries.labels.template.padding(0, 0, 0, 0);
            pieSeries.ticks.template.disabled = true;

            pieSeries.hiddenState.properties.endAngle = -90;
            pieSeries.hiddenState.properties.startAngle = -90;

            chart.legend = new am4charts.Legend();
            chart.legend.position = 'bottom';
            chart.legend.valign = 'bottom';
            chart.legend.contentAlign = 'center';
            chart.legend.labels.template.fontSize = 12;
            chart.legend.labels.template.fill = am4core.color('#000');
            chart.legend.itemContainers.template.paddingTop = 0;
            chart.legend.itemContainers.template.paddingBottom = 0;
            chart.legend.marginTop = 20;
            chart.legend.valueLabels.template.disabled = true;

            // Custom marker style
            chart.legend.markers.template.width = 12;
            chart.legend.markers.template.height = 12;
            let markerTemplate =
                chart.legend.markers.template.children.getIndex(
                    0,
                ) as am4core.RoundedRectangle;
            markerTemplate.cornerRadius(4, 4, 4, 4);

            const activeTitle = chart.seriesContainer.createChild(
                am4core.Label,
            );
            activeTitle.text = 'Active';
            activeTitle.fontSize = 13;
            activeTitle.fill = am4core.color('#000');
            activeTitle.horizontalCenter = 'middle';
            activeTitle.verticalCenter = 'middle';
        }

        // Create Idle chart (index 1)
        if (donutCharts[1]) {
            const idleData = donutCharts[1];
            const chart2 = am4core.create('pieChart2', am4charts.PieChart);
            chart2.padding(5, 10, 5, 0);

            chart2.data = [
                {
                    category: 'Healthy',
                    value: idleData.healthyPercentage,
                    color: am4core.color('#4CAF50'),
                },
                {
                    category: 'Unhealthy',
                    value: idleData.unhealthyPercentage,
                    color: am4core.color('#FF5733'),
                },
            ];

            chart2.innerRadius = am4core.percent(50);

            const pieSeries2 = chart2.series.push(new am4charts.PieSeries());
            pieSeries2.dataFields.value = 'value';
            pieSeries2.dataFields.category = 'category';
            pieSeries2.slices.template.stroke = am4core.color('#fff');
            pieSeries2.slices.template.strokeWidth = 2;
            pieSeries2.slices.template.strokeOpacity = 1;
            pieSeries2.slices.template.propertyFields.fill = 'color';
            pieSeries2.labels.template.text =
                "{values.value.percent.formatNumber('#.#')}%";
            pieSeries2.alignLabels = false;
            pieSeries2.labels.template.padding(0, 0, 0, 0);
            pieSeries2.ticks.template.disabled = true;

            pieSeries2.hiddenState.properties.endAngle = -90;
            pieSeries2.hiddenState.properties.startAngle = -90;

            chart2.legend = new am4charts.Legend();
            chart2.legend.position = 'bottom';
            chart2.legend.valign = 'bottom';
            chart2.legend.contentAlign = 'center';
            chart2.legend.labels.template.fontSize = 12;
            chart2.legend.labels.template.fill = am4core.color('#000');
            chart2.legend.itemContainers.template.paddingTop = 0;
            chart2.legend.itemContainers.template.paddingBottom = 0;
            chart2.legend.marginTop = 20;
            chart2.legend.valueLabels.template.disabled = true;

            // Custom marker style
            chart2.legend.markers.template.width = 12;
            chart2.legend.markers.template.height = 12;
            let markerTemplate2 =
                chart2.legend.markers.template.children.getIndex(
                    0,
                ) as am4core.RoundedRectangle;
            markerTemplate2.cornerRadius(4, 4, 4, 4);

            const idleText = chart2.seriesContainer.createChild(am4core.Label);
            idleText.text = 'Idle';
            idleText.fontSize = 13;
            idleText.fill = am4core.color('#000');
            idleText.horizontalCenter = 'middle';
            idleText.verticalCenter = 'middle';
        }

        removeAmChartLogo(this.elementRef.nativeElement);
    }
}
